import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock3, LayoutDashboard, Loader2, RefreshCw, ShieldX } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { socialAPI } from '../lib/api';
import { Button } from './ui/button';
import { SOCIAL_PRODUCT_NAME, SocialBrandLockup } from './SocialBrand';

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
}

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

export default function SocialAccessBoundary({ children }) {
  const { user, loading: authLoading } = useAuth();
  const userId = String(user?.id || user?._id || '');
  const [access, setAccess] = useState(null);
  const [checking, setChecking] = useState(Boolean(userId));
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState('');

  const checkAccess = useCallback(async ({ quiet = false } = {}) => {
    if (!userId) {
      setAccess(null);
      setChecking(false);
      return;
    }
    if (!quiet) setChecking(true);
    try {
      const { data } = await socialAPI.getMyAccess();
      setAccess(data || null);
      setError('');
    } catch (err) {
      setError(apiError(err, `Could not check your ${SOCIAL_PRODUCT_NAME} access.`));
    } finally {
      if (!quiet) setChecking(false);
    }
  }, [userId]);

  useEffect(() => {
    if (authLoading) return undefined;
    checkAccess();
    return undefined;
  }, [authLoading, checkAccess]);

  useEffect(() => {
    if (!userId) return undefined;
    const refreshVisibleAccess = () => {
      if (document.visibilityState === 'visible') checkAccess({ quiet: true });
    };
    const timer = window.setInterval(refreshVisibleAccess, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', refreshVisibleAccess);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshVisibleAccess);
    };
  }, [checkAccess, userId]);

  const acknowledge = async () => {
    setAcknowledging(true);
    setError('');
    try {
      await socialAPI.acknowledgeWarning();
      await checkAccess({ quiet: true });
    } catch (err) {
      setError(apiError(err, 'Could not acknowledge this warning. Please try again.'));
    } finally {
      setAcknowledging(false);
    }
  };

  if (authLoading || checking) {
    return <div className="flex min-h-screen items-center justify-center bg-[#050505] text-white"><Loader2 className="h-7 w-7 animate-spin text-[#a3a3a3]" aria-label={`Checking ${SOCIAL_PRODUCT_NAME} access`} /></div>;
  }

  const warning = access?.social_warning;
  const restriction = access?.social_suspension;
  if (!warning && !restriction) return children;

  const banned = restriction?.type === 'ban';
  const Icon = warning ? AlertTriangle : banned ? ShieldX : Clock3;
  const title = warning
    ? `You received a ${SOCIAL_PRODUCT_NAME} warning`
    : banned
      ? `Your ${SOCIAL_PRODUCT_NAME} access is banned`
      : `Your ${SOCIAL_PRODUCT_NAME} access is suspended`;
  const reason = warning?.reason || restriction?.reason || 'No additional reason was provided.';

  return (
    <div className="min-h-screen bg-[#050505] bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.07),transparent_34rem)] px-4 py-8 text-[#f5f5f5]">
      <div className="mx-auto max-w-xl">
        <div className="flex justify-center"><SocialBrandLockup /></div>
        <main className="mt-8 overflow-hidden rounded-3xl border border-[#303030] bg-[#101010] shadow-2xl shadow-black/50">
          <div className="border-b border-[#2a2a2a] p-6 sm:p-8">
            <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${warning ? 'bg-amber-500/10 text-amber-300' : 'bg-red-500/10 text-red-300'}`}><Icon className="h-7 w-7" /></span>
            <p className="mt-6 text-xs font-extrabold uppercase tracking-[0.18em] text-[#8a8a8a]">Account notice</p>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-white sm:text-3xl">{title}</h1>
            <div className="mt-5 rounded-2xl border border-[#333333] bg-black p-4">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8a8a8a]">Why this happened</p>
              <p className="mt-2 whitespace-pre-wrap text-[15px] leading-6 text-[#e5e5e5]">{reason}</p>
            </div>

            {warning ? (
              <p className="mt-5 text-sm leading-6 text-[#a3a3a3]">Review the <Link to="/terms-of-service" className="font-semibold text-white underline underline-offset-4">Terms of Service</Link> and <Link to="/community-guidelines" className="font-semibold text-white underline underline-offset-4">Community Guidelines</Link>. You must acknowledge this warning before returning to the app.</p>
            ) : banned ? (
              <p className="mt-5 text-sm leading-6 text-[#a3a3a3]">This is a permanent {SOCIAL_PRODUCT_NAME}-only ban. Access will return only if a Record Base moderator restores it.</p>
            ) : (
              <p className="mt-5 text-sm leading-6 text-[#a3a3a3]">Your suspension ends <strong className="text-white">{formatDate(restriction?.suspended_until)}</strong>. This screen checks automatically and will let you back in when the suspension is over.</p>
            )}

            {error ? <p role="alert" className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</p> : null}
          </div>

          <div className="flex flex-col gap-3 bg-[#0a0a0a] p-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-[#737373]">Only {SOCIAL_PRODUCT_NAME} is affected. Your distribution, catalog, billing, and main Record Base account remain available.</p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Link to="/dashboard"><Button variant="outline" className="rounded-full border-[#3a3a3a] bg-transparent text-white hover:bg-white/10"><LayoutDashboard className="mr-2 h-4 w-4" />TRB Dashboard</Button></Link>
              {warning ? (
                <Button type="button" onClick={acknowledge} disabled={acknowledging} className="rounded-full bg-white px-5 font-bold text-black hover:bg-[#d7dbdc]">{acknowledging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}I understand the Terms of Service</Button>
              ) : (
                <Button type="button" onClick={() => checkAccess()} disabled={checking} className="rounded-full bg-white px-5 font-bold text-black hover:bg-[#d7dbdc]"><RefreshCw className={`mr-2 h-4 w-4 ${checking ? 'animate-spin' : ''}`} />Check access</Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
